/*
 *
 * File: Header.jsx
 *
 * Purpose: Passenger booking page header component.
 *
 * Author:   Thomas Burgard (Software development)
 * Created:  23.02.2022
 * Modified: 20.04.2023
 *           - Module header comments changed
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import React from "react";
import { useTranslation } from "react-i18next";
import { Menubar } from "primereact/menubar";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { changeLanguage } from "../../store/actions";

//--------------------------------------------------------------
//--- Variable declarations ------------------------------------
//--------------------------------------------------------------
const USER_LOGIN = "userLogin";
let username = null;
//--- End component variable declarations ----------------------

/**
 * Implementation of Passenger app header (with menu bar) component.
 *
 * @param {*} props user data
 * @returns JSX-HTML-DOM
 */
const Header = (props) => {
	const userData = props.userData;
	const navigate = useNavigate();
	const { t, i18n } = useTranslation("common");
	const dispatch = useDispatch();
	const whiteLabel = props.userData.regData.WHITELABEL;
	let items;

	username = props.userData.regData.BenutzerName;

	//--------------------------------------------------------------
	// --- MenuBar items array
	//--------------------------------------------------------------
	if (whiteLabel === "1") {
		items = [
			{
				label: t("navbar.nav-language"),
				items: [
					{
						label: t("navbar.nav-de"),
						command: () => {
							i18n.changeLanguage("de");
							dispatch(changeLanguage({ language: "de" }));
						},
					},
					{
						label: t("navbar.nav-en"),
						command: () => {
							i18n.changeLanguage("en");
							dispatch(changeLanguage({ language: "en" }));
						},
					},
				],
			},
			{
				label: t("navbar.nav-booking"),
				icon: "pi pi-fw pi-car",
				command: () => {
					//routeChange("/multistop");
					routeChange("/");
				},
			},
			{
				label: t("navbar.nav-change-booking"),
				icon: "pi pi-fw pi-pencil",
				command: () => {
					routeChange("/changeyourbooking");
				},
			},
		];
	} else {
		items = [
			{
				label: t("navbar.nav-language"),
				items: [
					{
						label: t("navbar.nav-de"),
						command: () => {
							i18n.changeLanguage("de");
							dispatch(changeLanguage({ language: "de" }));
						},
					},
					{
						label: t("navbar.nav-en"),
						command: () => {
							i18n.changeLanguage("en");
							dispatch(changeLanguage({ language: "en" }));
						},
					},
				],
			},
			{
				label: t("navbar.nav-booking"),
				icon: "pi pi-fw pi-car",
				command: () => {
					//routeChange("/multistop");
					routeChange("/");
				},
			},
			{
				label: t("navbar.nav-change-booking"),
				icon: "pi pi-fw pi-pencil",
				command: () => {
					routeChange("/changeyourbooking");
				},
			},
			{
				label: t("navbar.nav-company"),
				icon: "pi pi-fw pi-info-circle",
				items: [
					{
						label: t("navbar.nav-customerinfo"),
						command: () => {
							//routeChange("/multistop/customerinfo");
							routeChange("/customerinfo");
						},
					},
					{
						label: t("navbar.nav-legalnotice"),
						command: () => {
							//routeChange("/multistop/legalnotice");
							routeChange("/legalnotice");
						},
					},
				],
			},
			/* {
        label: t("navbar.nav-login"),
        icon: "pi pi-fw pi-sign-in",
        command: () => {
          routeChange(USER_LOGIN);
        },
      }, */
		];
	}

	/**
	 * Change the route with given route action code.
	 *
	 * @param {*} route route action code
	 */
	const routeChange = (route) => {
		if (route === USER_LOGIN) {
			window.location =
				"https://www." + username + ".drivepartner.com/login_driver.php";
		} else {
			navigate(route);
		}
	};

	/**
	 * Set header logo, dependent on username.
	 *
	 * @returns JSX-Code
	 */
	const setHeaderLogo = () => {
		let start = null;

		switch (username) {
			case "prestigechauffeurs":
				start = (
					<a
						href="https://www.prestigechauffeurs.de/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="Prestige Chauffeurs"
							title="Prestige Chauffeurs"
							src="img/logo_prestigechauffeurs.jpg"
							width="160"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "Fahrservice-Greiter":
				start = (
					<a
						href="https://www.fahrservice-greiter.de/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="Fahrservice Greiter"
							title="Fahrservice Greiter"
							src="img/logo_fahrservice_greiter.png"
							width="155"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "royal-transfer":
				start = (
					<a
						href="https://www.royal-transfer.de/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="Royal-Transfer"
							title="Royal-Transfer"
							src="img/logo_royaltransfer_1161.jpg"
							width="160"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "cs4you":
				start = (
					<a
						href="https://www.chauffeurservice4you.de/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="Chauffeurservice4you"
							title="Chauffeurservice4you"
							src="img/logo_cs4you_500.jpg"
							width="160"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "moudy":
				start = (
					<img
						alt="Moudy"
						title="Moudy"
						src="img/logo_moudy_700.jpg"
						width="160"
						className="mr-3"
					></img>
				);
				break;
			case "chauffeur-ffm":
				start = (
					<a href="https://chauffeur-ffm.de/" target="_blank" rel="noreferrer">
						<img
							alt="Chauffeur-Ffm"
							title="Chauffeur-Ffm"
							src="img/logo_chauffeur-ffm.jpg"
							width="160"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "bavarios":
				start = (
					<a href="https://www.bavarios.de" target="_blank" rel="noreferrer">
						<img
							alt="Bavarios"
							title="Bavarios"
							src="img/logo_bavarios.png"
							width="160"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "shekireisen":
				start = (
					<a
						href="https://www.shekireisen.de/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="Shekireisen"
							title="Shekireisen"
							src="img/logo_shekireisen.png"
							width="100"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "Vipkurier":
				start = (
					<a href="https://www.vipkurier.eu/" target="_blank" rel="noreferrer">
						<img
							alt="Vipkurier"
							title="Vipkurier"
							src="img/logo_vipkurier.jpg"
							width="100"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "taxi-blacksmith":
				start = (
					<a href="https://taxi-blacksmith.de" target="_blank" rel="noreferrer">
						<img
							alt="Taxi-Blacksmith"
							title="Taxi-Blacksmith"
							src="img/logo_blacksmith.jpg"
							width="100"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "muster":
				start = (
					<a
						href="https://www.muster.drivepartner.com/home/muster/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="logo"
							src="img/logo_mustermann.jpg"
							width="160"
							className="mr-3"
						></img>
					</a>
				);
				break;
			case "elitesdriver":
				start = (
					<img
						alt="Elitesdriver"
						src="img/logo_elitesdriver.png"
						width="140"
						className="mr-3"
					></img>
				);
				break;
			default:
				start = (
					<a
						href="https://www.drivepartner.com/"
						target="_blank"
						rel="noreferrer"
					>
						<img
							alt="logo"
							src="img/logo_drivepartner_190.png"
							width="140"
							className="mr-3"
						></img>
					</a>
				);
				break;
		}

		if (whiteLabel === "1") start = null;

		return start;
	};

	//--------------------------------------------------------------
	//--- Returns JSX code
	//--------------------------------------------------------------
	return (
		<>
			<Menubar model={items} start={setHeaderLogo()} />
		</>
	);
};

export default Header;
